import { ChevronUp, ChevronDown } from "lucide-react";

// v1.0.17 Final Amendment — client-side Project Register sorting (no refetch, no extra queries).
// Rankings are deterministic and documented in PRD.md.
const STATUS_RANK = (p) => (p.status === "Completed" || p.archived ? 3 : (p.module_landing || {}).landed ? 2 : (p.module_landing || {}).expected_date ? 1 : 0); // Active → Scheduled Landing → Landed → Completed/Archived
const METER_RANK = { red: 0, orange: 1, purple: 2, blue: 3, green: 4 };
const SUPPLY_RANK = { grey: 0, orange: 1, red: 2, green: 3 };
const SOLAR_RANK = { none: 0, pending: 1, complete: 2 };
const PAYMENT_RANK = { no_claim: 0, partial_outstanding: 1, final_outstanding: 2, partial_paid: 3, paid: 4 };
const NBN_RANK = (p) => (p.nbn_infrastructure_complete ? 1 : 0) + (p.nbn_application_complete ? 1 : 0); // neither → one of two → both

const str = (v) => (v || "").toString().toLowerCase();
const addr = (p) => str(p.label || `${(p.address || {}).street || ""} ${(p.address || {}).suburb || ""}`);
const projectNoKey = (p) => [Number(p.tranche) || 0, Number(p.project_number) || 0, str(p.display_reference)]; // natural: tranche, then numeric project number

export const SORT_KEYS = {
  quotation_date: (p) => [p.quotation_date ? 0 : 1, p.quotation_date || ""],                       // missing dates always last
  project_no: projectNoKey,
  address: (p) => [addr(p)],
  status: (p) => [STATUS_RANK(p), (p.module_landing || {}).expected_date || "", addr(p)],
  meters: (p) => [METER_RANK[p.meter_connection_stage] ?? 0, p.dwellings_connected || 0, p.dwellings_required ?? p.dwellings_total ?? 0],
  supply: (p) => [SUPPLY_RANK[p.distributor_application_vqr_status] ?? 0, str(p.distributor), str(p.supply_type)],
  nbn: (p) => [NBN_RANK(p), p.nbn_infrastructure_complete ? 1 : 0, p.nbn_application_complete ? 1 : 0],
  solar: (p) => [SOLAR_RANK[p.solar_status] ?? 0],
  payment: (p) => [PAYMENT_RANK[p.payment_status] ?? 0],
  contract_sum: (p) => [Number(p.current_contract_sum) || 0],
};

function cmp(a, b) {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i], y = b[i];
    if (x === y) continue;
    if (typeof x === "number" && typeof y === "number") return x - y;
    return String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: "base" });
  }
  return 0;
}

export function sortProjects(projects, sort) {
  if (!projects || !sort) return projects; // accepted default ordering (server order) until a heading is clicked
  const keyFn = SORT_KEYS[sort.key];
  if (!keyFn) return projects;
  const decorated = projects.map((p, i) => ({ p, i, k: keyFn(p) }));
  decorated.sort((a, b) => (cmp(a.k, b.k) || a.i - b.i) * (sort.dir === "desc" ? -1 : 1));
  return decorated.map((d) => d.p);
}

// asc → desc → default (third click restores the accepted default ordering)
export const nextSort = (sort, key) => (!sort || sort.key !== key ? { key, dir: "asc" } : sort.dir === "asc" ? { key, dir: "desc" } : null);

export function SortButton({ col, label, sort, onSort, className = "", children, testId, size = "md" }) {
  const active = sort && sort.key === col;
  const dir = active ? sort.dir : null;
  return (
    <button type="button" data-testid={testId || `sort-${col}`} data-sort-dir={dir || "none"} onClick={() => onSort(nextSort(sort, col))}
      className={`group inline-flex w-full cursor-pointer items-center gap-0.5 rounded-sm text-left uppercase transition-colors hover:text-gray-950 focus:outline-none focus-visible:ring-1 focus-visible:ring-[#765297] ${className.includes("text-right") ? "justify-end" : className.includes("text-center") ? "justify-center" : ""} ${active ? "text-gray-950" : ""} ${size === "sm" ? "text-[9px] font-normal normal-case text-gray-400" : ""}`}
      style={{ font: size === "sm" ? undefined : "inherit", letterSpacing: "inherit", color: active ? undefined : "inherit" }}
      title={`Sort by ${label}`}>
      {children || label}
      <span className="inline-flex w-2.5 shrink-0 justify-center">
        {dir === "asc" && <ChevronUp size={11} strokeWidth={2.4} aria-hidden="true" />}
        {dir === "desc" && <ChevronDown size={11} strokeWidth={2.4} aria-hidden="true" />}
        {!dir && <ChevronUp size={11} strokeWidth={2} aria-hidden="true" className="opacity-0 transition-opacity group-hover:opacity-30" />}
      </span>
    </button>
  );
}

export function SortTh({ col, label, sort, onSort, className = "", children, testId }) {
  const active = sort && sort.key === col;
  const dir = active ? sort.dir : null;
  return (
    <th className={className} aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      <SortButton col={col} label={label} sort={sort} onSort={onSort} className={className} testId={testId}>{children}</SortButton>
    </th>
  );
}
